import LogRocket from 'logrocket'
import Cookie from 'js-cookie'
import apis from 'browser/app/models/apis'
import { AbstractSessionRecorder } from 'shared-libs/models/abstract-session-recorder'
import { isHeadless } from '../utils/utils'
import { requestSanitizer, responseSanitizer } from 'shared-libs/helpers/remote-monitoring'
import { ConfigKey, getConfig } from 'shared-libs/models/config'

// Captured synchronously at module load — before any login flow runs.
// If no auth cookie exists at this point, the user arrived via the login page
// (post-logout reload) and needs a clean session. If the cookie is present,
// the user was already logged in (page refresh) and should continue their session.
const hadAuthCookieAtPageLoad = !!Cookie.get('Authorization')
console.log('[SessionRecorder] Page load auth check:', {
  hadAuthCookieAtPageLoad,
  forceCleanStart: !hadAuthCookieAtPageLoad,
})

class SessionRecorderImpl extends AbstractSessionRecorder {
  private interactionListenerCleanup: (() => void) | null = null

  /**
   * Called on sign-out. Reloads the page if session recording was active, which
   * is the only way to fully reset LogRocket (it has no stop API). If recording
   * was never enabled (headless, admin, feature off), no reload happens and the
   * existing sign-out behavior is preserved.
   */
  public reset(): void {
    console.log('[SessionRecorder] reset()', { wasEnabled: this.isEnabled })
    if (this.isEnabled) {
      console.log('[SessionRecorder] Reloading page to fully reset LogRocket')
      window.location.reload()
    }
  }

  /** Stop recording and shut down LogRocket */
  public async stop(): Promise<void> {
    // If we're still waiting for a first interaction, cancel that
    if (this.interactionListenerCleanup) {
      this.interactionListenerCleanup()
      this.interactionListenerCleanup = null
    }

    if (this.isInit) {
      try {
        // LogRocket.uninstall() permanently stops all capture (DOM, network, console).
        // It is a one-way operation — init() becomes a no-op afterward with no way to
        // restart within the same page load. This is intentional: signout() performs a
        // full page reload after OAuth revoke, so LogRocket is freshly initialized on
        // the next login without carrying over any previous user's session data.
        // @ts-expect-error uninstall() exists in the bundle but is not in the type definitions
        LogRocket.uninstall?.()
      } catch (err) {
        console.error('[SessionRecorder] Failed to shutdown:', err)
      }
    }

    this.isInit = false
    this.isRecording = false
    console.log('[SessionRecorder] Shutdown complete')
  }

  /** Start recording — deferred until first user interaction to avoid billing for bounces/bots */
  public async start(): Promise<void> {
    await this.ensureInteractionListenerRegistered()
  }

  /** Get the current session URL */
  public getSessionUrl(callback: (url: string) => void): void {
    try {
      LogRocket.getSessionURL(callback)
    } catch (err) {
      console.error('[SessionRecorder] Failed to get session URL:', err)
    }
  }

  /**
   * Emit a custom LogRocket event through a single guarded entry point. Other
   * modules (e.g. upload-worker) MUST call this instead of `LogRocket.track()`
   * directly, so the "is recording active?" check lives in exactly one place.
   * When recording hasn't started — disabled firm, admin, headless, or simply
   * before the first user interaction — this is a no-op. Without the guard,
   * track() calls queue into LogRocket's buffers for sessions that will never be
   * recorded or uploaded, which is what drove the unbounded buffer growth.
   */
  public track(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.isRecording) return
    try {
      LogRocket.track(eventName, properties as Parameters<typeof LogRocket.track>[1])
    } catch (err) {
      console.error('[SessionRecorder] Failed to track event:', err)
    }
  }

  /** Identify the current user to LogRocket. Called once per init. */
  private identifyUser(): void {
    const settings = this.settings ?? apis.getSettings()
    const identity = settings.buildUserIdentity()

    try {
      LogRocket.identify(identity.userId, identity)
      console.log('[SessionRecorder] User identity:', identity)
    } catch (err) {
      console.error('[SessionRecorder] Failed to identify user:', err)
    }
  }

  /**
   * Register a one-time interaction listener that initializes LogRocket on first user action.
   * This avoids creating billable sessions for page loads that immediately bounce
   * (e.g. uptime monitors, push notification opens, deep link closes).
   */
  private async ensureInteractionListenerRegistered(): Promise<void> {
    if (this.isInit || this.interactionListenerCleanup) return

    this.settings = apis.getSettings()
    const headless = isHeadless()
    const sessionReplayEnabled = getConfig(
      this.settings,
      ConfigKey.SESSION_REPLAY_ENABLED,
      false
    )
    const isAdmin = this.settings.isAdmin

    this.isEnabled = sessionReplayEnabled && !headless && !isAdmin

    if (!this.isEnabled) {
      console.info('[SessionRecorder] Disabled', { headless, sessionReplayEnabled, isAdmin })
      return
    }

    const interactionEvents = ['click', 'touchstart'] as const
    const handler = () => {
      if (this.isInit) return
      this.interactionListenerCleanup?.()
      this.interactionListenerCleanup = null
      this.initLogRocket()
    }
    interactionEvents.forEach(e => window.addEventListener(e, handler, { passive: true }))
    this.interactionListenerCleanup = () => {
      interactionEvents.forEach(e => window.removeEventListener(e, handler))
    }
    console.log('[SessionRecorder] Waiting for first interaction before initializing')
  }

  /** Initialize LogRocket and begin recording */
  private initLogRocket(): void {
    try {
      // Refresh settings at init time — this.settings was captured when start() was
      // called but initLogRocket() fires on first interaction which may be much later.
      this.settings = apis.getSettings()
      const userTraits = this.settings?.buildUserIdentity()
      /*
       * The following settings reference visible and hidden configuration options
       * from the LogRocket source code.
       *
       * If we encounter performance issues, we may need to adjust hidden parameters
       * such as `runLoopInterval` to reduce the polling frequency.
       *
       * LogRocket default configuration from its source code:
       *
       *   this.config = {
       *     disableWebViewConnection: false,
       *     shouldConnectTabs: true,
       *     iframes: {
       *       merge: false,
       *       crossOrigin: {}
       *     },
       *     activeUserPingTimeout: 5000,
       *     connectedTabCheckInterval: 100,
       *     newSessionTimeThreshold: 120000,
       *     inactivityTimeThreshold: 1800000,
       *     runLoopInterval: 500,
       *     uploadTimeInterval: 2750,
       *     lookbackLoopInterval: 1000,
       *     limitedLookbackSize: 10000000
       *   }
       */
      console.log('[SessionRecorder] Calling LogRocket.init()', { forceCleanStart: !hadAuthCookieAtPageLoad })
      // `forceCleanStart` and `newSessionTimeThreshold` are valid LogRocket options but
      // are missing from the published type definitions, so we widen the call signature
      // via an intersection rather than @ts-expect-error (which behaves inconsistently
      // across TS versions when multiple excess properties are present).
      type ExtendedOptions = Parameters<typeof LogRocket.init>[1] & {
        forceCleanStart?: boolean
        newSessionTimeThreshold?: number
      }
      const initOptions: ExtendedOptions = {
        release: userTraits.release,
        rootHostname: apis.getStore().getDomain(),
        network: { requestSanitizer, responseSanitizer },
        console: {
          isEnabled: { debug: false },
          shouldAggregateConsoleErrors: true,
        },
        uploadTimeInterval: 10 * 1000,
        // No auth cookie at page load means the user arrived via the login page —
        // force a clean session so we don't inherit a previous user's session window.
        forceCleanStart: !hadAuthCookieAtPageLoad,
        // Increase from the 2-minute default to reduce session fragmentation for field workers
        // who put their device down between tasks. Hidden config option (see source above).
        newSessionTimeThreshold: 10 * 60 * 1000,
      }
      LogRocket.init(this.settings.logRocketAppId, initOptions)

      this.isInit = true
      this.identifyUser()
      this.isRecording = true
      LogRocket.getSessionURL(url => console.log('[SessionRecorder] Session URL:', url))
      console.log('[SessionRecorder] Initialized and recording started')
    } catch (err) {
      console.error('[SessionRecorder] Initialization failed:', err)
      this.isEnabled = false
    }
  }
}

export const SessionRecorder = new SessionRecorderImpl()
